import { Add, ArrowDownward, ArrowUpward, Close } from '@mui/icons-material';
import { Autocomplete, IconButton, TextField, Tooltip, Typography } from '@mui/material';
import {
  Control,
  Controller,
  FieldErrors,
  useFieldArray,
  UseFormRegister,
  useWatch,
} from 'react-hook-form';

import { Button } from '@/components/Button';
import { useStatuses } from '@/tracker/features/taskFilters';

import { BoardSettingsFormValues } from '../types';

import s from './BoardColumnsEditor.module.scss';

type Props = {
  control: Control<BoardSettingsFormValues>;
  register: UseFormRegister<BoardSettingsFormValues>;
  errors: FieldErrors<BoardSettingsFormValues>;
};

/** Колонки доски: название и статусы каждой, порядок, добавление и удаление */
const BoardColumnsEditor: React.FC<Props> = ({ control, register, errors }) => {
  const statuses = useStatuses(true);
  const { fields, append, remove, move } = useFieldArray({ control, name: 'columns' });
  const columns = useWatch({ control, name: 'columns' });

  // Статус стоит максимум в одной колонке — в остальных его не предлагаем
  const columnOfStatus = new Map<string, number>();

  columns.forEach((column, index) =>
    column.statuses.forEach((key) => columnOfStatus.set(key, index)),
  );

  return (
    <>
      <Typography variant="subtitle2">Колонки</Typography>
      {!fields.length && (
        <Typography variant="body2" color="text.secondary">
          Карточка стоит в колонке по статусу задачи. Без колонок доска пустая
        </Typography>
      )}
      {fields.map((field, index) => (
        <div
          key={field.id}
          className={s.column}
          // Enter в поиске статусов без совпадений иначе отправил бы всю форму
          onKeyDown={(event) => event.key === 'Enter' && event.preventDefault()}
        >
          <TextField
            size="small"
            placeholder="Название колонки"
            error={!!errors.columns?.[index]?.name}
            slotProps={{ htmlInput: { 'aria-label': `Название колонки ${index + 1}` } }}
            {...register(`columns.${index}.name`, { validate: (value) => !!value.trim() })}
          />
          <Controller
            control={control}
            name={`columns.${index}.statuses`}
            render={({ field: statusesField }) => (
              <Autocomplete
                multiple
                size="small"
                disableCloseOnSelect
                options={statuses}
                value={statuses.filter((status) => statusesField.value.includes(status.key))}
                onChange={(_, value) => statusesField.onChange(value.map(({ key }) => key))}
                getOptionLabel={(status) => status.name}
                getOptionDisabled={(status) => (columnOfStatus.get(status.key) ?? index) !== index}
                isOptionEqualToValue={(option, value) => option.key === value.key}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder={statusesField.value.length ? '' : 'Статусы'}
                    slotProps={{
                      htmlInput: {
                        ...params.inputProps,
                        'aria-label': `Статусы колонки ${index + 1}`,
                      },
                    }}
                  />
                )}
              />
            )}
          />
          <div className={s.column__actions}>
            <Tooltip title="Выше">
              <span>
                <IconButton
                  size="small"
                  aria-label="Выше"
                  disabled={index === 0}
                  onClick={() => move(index, index - 1)}
                >
                  <ArrowUpward fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Ниже">
              <span>
                <IconButton
                  size="small"
                  aria-label="Ниже"
                  disabled={index === fields.length - 1}
                  onClick={() => move(index, index + 1)}
                >
                  <ArrowDownward fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Удалить колонку">
              <IconButton size="small" aria-label="Удалить колонку" onClick={() => remove(index)}>
                <Close fontSize="small" />
              </IconButton>
            </Tooltip>
          </div>
        </div>
      ))}
      <Button
        variant="text"
        startIcon={<Add />}
        sx={{ alignSelf: 'flex-start' }}
        onClick={() => append({ name: '', statuses: [] })}
      >
        Добавить колонку
      </Button>
    </>
  );
};

export { BoardColumnsEditor };
